import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button, Input, Card, CardContent, CardHeader, CardTitle } from './ui';

const LicenseTypeModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  editingLicenseType = null,
  serverBanner = '',
  serverFieldErrors = {},
  onDismissServerErrors,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    is_trial_type: false,
  });

  React.useEffect(() => {
    if (editingLicenseType) {
      setFormData({
        name: editingLicenseType.name || '',
        description: editingLicenseType.description || '',
        is_active: editingLicenseType.is_active !== undefined ? editingLicenseType.is_active : true,
        is_trial_type: Boolean(editingLicenseType.is_trial_type),
      });
    } else {
      setFormData({
        name: '',
        description: '',
        is_active: true,
        is_trial_type: false,
      });
    }
  }, [editingLicenseType, isOpen]);

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    onDismissServerErrors?.();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit({ ...formData });
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      is_active: true,
      is_trial_type: false,
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingLicenseType ? 'Editar Tipo de Licencia' : 'Nuevo Tipo de Licencia'}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {serverBanner && (
            <div
              role="alert"
              className="rounded-md bg-red-50 border border-red-200 p-3 text-red-800 text-sm whitespace-pre-wrap"
            >
              {serverBanner}
            </div>
          )}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Información</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del tipo *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Ej: Básica, Premium, Empresarial"
                  className={errors.name || serverFieldErrors.name ? 'border-red-500' : ''}
                />
                {(errors.name || serverFieldErrors.name) && (
                  <p className="text-red-500 text-sm mt-1">{errors.name || serverFieldErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Descripción opcional"
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    serverFieldErrors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {serverFieldErrors.description && (
                  <p className="text-red-500 text-sm mt-1">{serverFieldErrors.description}</p>
                )}
              </div>

              <p className="text-sm text-gray-600">
                Precio, promociones, vigencia e instalaciones se definen al crear o editar cada licencia, no aquí.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Estado</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Tipo activo
                </label>
              </div>
              <div>
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    name="is_trial_type"
                    id="license-type-is-trial"
                    checked={formData.is_trial_type}
                    onChange={handleInputChange}
                    className="h-4 w-4 mt-0.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div className="ml-2">
                    <label
                      htmlFor="license-type-is-trial"
                      className="text-sm font-medium text-gray-700 cursor-pointer"
                    >
                      Política trial (misma PC, varias licencias)
                    </label>
                    <p className="text-sm text-gray-500 mt-1">
                      Permite que el mismo equipo (misma MAC) active otra licencia trial de este
                      cliente para otro producto. Solo aplica cuando ambas licencias usan un tipo
                      con esta opción.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
            >
              {isLoading
                ? (editingLicenseType ? 'Actualizando...' : 'Creando...')
                : (editingLicenseType ? 'Actualizar' : 'Crear')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LicenseTypeModal;
